import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  LinearProgress,
  Button,
  Divider,
  Tooltip,
  Alert
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HistoryIcon from '@mui/icons-material/History';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LaunchIcon from '@mui/icons-material/Launch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { get } from '../../services/apiService';
import {
  ASSESSMENT_META,
  GRADE_COLORS,
  RISK_CATEGORIES
} from './shaReportData';

const DARK_GREEN = '#1D4434';
// Distinct accent for the Planning & Progress hero so it reads as "delivery /
// work-in-flight" at a glance and doesn't blend into the page's dark-green chrome
// or the amber grade card.
const PLANNING_BLUE = '#1565C0'; // MUI blue[800]
const PLANNING_BLUE_DARK = '#0D3F7E';

// ---------- small presentational helpers ----------

/**
 * Coloured letter-grade badge matching the SHA Report visual language.
 * Two sizes: 'sm' (used inline next to category name), 'lg' (hero).
 */
function GradeBadge({ grade, size = 'sm' }) {
  const colour = GRADE_COLORS[grade] || GRADE_COLORS.C;
  const dim = size === 'lg' ? 64 : 28;
  return (
    <Avatar
      sx={{
        width: dim,
        height: dim,
        bgcolor: colour.bg,
        color: colour.fg,
        fontWeight: 700,
        fontSize: size === 'lg' ? 32 : 14,
        border: `2px solid ${colour.main}`,
        boxShadow: size === 'lg' ? `0 0 0 4px ${colour.main}25` : 'none'
      }}
    >
      {grade}
    </Avatar>
  );
}

/**
 * Donut with completion % in center — reused language from Planning & Progress /
 * HIPAA/CUI views so operators read the two views the same way.
 */
function CompletionDonut({ percentage = 0, size = 72, thickness = 5 }) {
  const value = Math.min(Math.max(Number(percentage) || 0, 0), 100);
  const color = value < 33 ? 'error' : value < 67 ? 'warning' : 'success';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress variant="determinate" value={value} color={color} size={size} thickness={thickness} />
      <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          {Math.round(value)}%
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Compact stat row used inside the hero cards (icon + label + value).
 */
function HeroStat({ icon: Icon, label, value, color = DARK_GREEN }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Icon sx={{ fontSize: 16, color }} />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {value}
      </Typography>
    </Stack>
  );
}

function getAdoUrl(workItemId) {
  if (!workItemId) return '#';
  const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
  const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
  const baseUrl = adoOrg.includes('visualstudio.com')
    ? `https://${adoOrg}`
    : `https://dev.azure.com/${adoOrg}`;
  return `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${workItemId}`;
}

// ---------- overview ----------

/**
 * SHA > Overview — compact dashboard that surfaces the two big things an
 * operator comes to this page for:
 *
 *   1. Epic progress for the SHA delivery work (from Planning & Progress).
 *   2. Current assessment grade snapshot (from the static Report data).
 *
 * Each hero links into its tab for the full view, so Overview stays a
 * read-only "at a glance" panel and doesn't duplicate tab functionality.
 */
const SHAOverview = () => {
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await get('/api/sha/planning-progress/dashboard');
      if (res?.success) setDashboard(res.data);
    } catch (err) {
      console.error('Error loading SHA overview dashboard:', err);
      setError(err.message || 'Failed to load epic progress');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const goToTab = useCallback(
    (tab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      });
    },
    [setSearchParams]
  );

  // --- derived: report rollups (computed once from static data) ---
  const gradeDistribution = useMemo(() => {
    const counts = { A: 0, B: 0, C: 0, D: 0, F: 0 };
    for (const c of RISK_CATEGORIES) {
      if (counts[c.grade] != null) counts[c.grade] += 1;
    }
    return counts;
  }, []);
  const totalFindings = useMemo(
    () => RISK_CATEGORIES.reduce((s, c) => s + (c.findings?.total || 0), 0),
    []
  );
  const totalCriticalHigh = useMemo(
    () => RISK_CATEGORIES.reduce((s, c) => s + (c.findings?.criticalHigh || 0), 0),
    []
  );

  const overallColour = GRADE_COLORS[ASSESSMENT_META.overallGrade] || GRADE_COLORS.C;

  // --- derived: planning rollups ---
  const completion = dashboard?.avgCompletionRate ?? 0;
  const totalFeatures = dashboard?.totalFeatures ?? 0;
  const totalSP = dashboard?.totalStoryPoints ?? 0;
  const completedSP = dashboard?.completedStoryPoints ?? 0;
  const byState = dashboard?.byState || {};
  const epicId = dashboard?.epicId;
  const epicTitle = dashboard?.epicTitle;

  // Normalise state buckets into a stable, friendly order.
  const stateOrder = ['New', 'Active', 'In Progress', 'Committed', 'Resolved', 'Closed', 'Done', 'Removed'];
  const stateEntries = useMemo(() => {
    const keys = Object.keys(byState);
    const sorted = [
      ...stateOrder.filter((s) => keys.includes(s)),
      ...keys.filter((s) => !stateOrder.includes(s))
    ];
    return sorted.map((s) => [s, byState[s]]);
  }, [byState]);

  return (
    <Box>
      {/* ============ HERO ROW: Planning progress + Assessment grade ============
           mb: 3 gives clear breathing room before "Risk categories" below, and
           overflow:'hidden' on each hero Paper ensures long epic titles can never
           bleed past the card boundary into adjacent content. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* --- Planning & Progress hero (blue accent, distinct from page theme) --- */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              height: '100%',
              overflow: 'hidden',
              borderColor: `${PLANNING_BLUE}55`,
              background: `linear-gradient(135deg, ${PLANNING_BLUE}12 0%, ${PLANNING_BLUE}03 100%)`,
              display: 'flex',
              flexDirection: 'column',
              // Subtle left-edge accent so the card colour is unmistakable even
              // when the background tint is desaturated by the user's theme.
              borderLeft: `4px solid ${PLANNING_BLUE}`
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <AssignmentIcon sx={{ color: PLANNING_BLUE, fontSize: 20 }} />
              <Typography
                variant="overline"
                sx={{ color: PLANNING_BLUE_DARK, fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1 }}
              >
                Planning &amp; Progress
              </Typography>
            </Stack>

            {loading ? (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight={150}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Alert severity="warning" variant="outlined" sx={{ my: 1 }}>
                {error}
              </Alert>
            ) : (
              <>
                <Stack direction="row" spacing={2} alignItems="center" mb={1.5} sx={{ minWidth: 0 }}>
                  <CompletionDonut percentage={completion} size={72} thickness={5} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      Epic
                    </Typography>
                    {epicId ? (
                      <Tooltip title={`#${epicId}${epicTitle ? ` — ${epicTitle}` : ''} · Open in Azure DevOps`}>
                        {/*
                          display:'flex' + minWidth:0 lets the inner title span shrink
                          and ellipsise correctly inside its flex parent. Using
                          inline-flex here would size to content and overflow the card.
                        */}
                        <Box
                          component="a"
                          href={getAdoUrl(epicId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            color: PLANNING_BLUE_DARK,
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            #{epicId}
                            {epicTitle ? ` — ${epicTitle}` : ''}
                          </Box>
                          <LaunchIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        (not configured)
                      </Typography>
                    )}
                  </Box>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={completion}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    mb: 1.5,
                    bgcolor: `${PLANNING_BLUE}1A`,
                    '& .MuiLinearProgress-bar': { bgcolor: PLANNING_BLUE }
                  }}
                />

                <Stack spacing={0.75} mb={1.5}>
                  <HeroStat
                    icon={AssignmentIcon}
                    label="Features"
                    value={totalFeatures}
                    color={PLANNING_BLUE}
                  />
                  <HeroStat
                    icon={BoltIcon}
                    label="Story points"
                    value={`${completedSP} / ${totalSP}`}
                    color={PLANNING_BLUE}
                  />
                  <HeroStat
                    icon={CheckCircleIcon}
                    label="Avg completion"
                    value={`${completion}%`}
                    color={PLANNING_BLUE}
                  />
                </Stack>

                {/* feature-state chips */}
                {stateEntries.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    {stateEntries.map(([state, count]) => (
                      <Chip
                        key={state}
                        label={`${state} · ${count}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          bgcolor: 'background.paper',
                          color: PLANNING_BLUE_DARK,
                          border: '1px solid',
                          borderColor: `${PLANNING_BLUE}40`
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}

            <Box sx={{ mt: 'auto' }}>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => goToTab('planning-progress')}
                sx={{
                  color: PLANNING_BLUE_DARK,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 0.5,
                  '&:hover': { bgcolor: `${PLANNING_BLUE}12` }
                }}
              >
                View Planning &amp; Progress
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* --- Assessment grade hero --- */}
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              height: '100%',
              overflow: 'hidden',
              borderColor: `${overallColour.main}50`,
              background: `linear-gradient(135deg, ${overallColour.bg} 0%, #ffffff 70%)`,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: `4px solid ${overallColour.main}`
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <AssessmentIcon sx={{ color: overallColour.main, fontSize: 20 }} />
              <Typography
                variant="overline"
                sx={{ color: overallColour.fg, fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1 }}
              >
                Assessment grade
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
              <GradeBadge grade={ASSESSMENT_META.overallGrade} size="lg" />
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                  {ASSESSMENT_META.product}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                  <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {ASSESSMENT_META.assessmentDate}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack spacing={0.75} mb={1.5}>
              <HeroStat
                icon={ReportProblemIcon}
                label="Findings"
                value={`${totalFindings} total · ${totalCriticalHigh} Critical/High`}
                color={overallColour.main}
              />
              <HeroStat
                icon={HistoryIcon}
                label="Prior SHA"
                value={`${ASSESSMENT_META.priorAssessment.grade} (${ASSESSMENT_META.priorAssessment.date}) · ${ASSESSMENT_META.priorAssessment.highCriticalOutstanding} H/C outstanding`}
                color="text.secondary"
              />
            </Stack>

            {/* mini grade-distribution bar (same visual as Report tab) */}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
              >
                Grade distribution ({RISK_CATEGORIES.length} categories)
              </Typography>
              <Stack direction="row" spacing={0.75} mt={0.75} flexWrap="wrap" useFlexGap>
                {Object.entries(gradeDistribution).map(([g, count]) => {
                  const c = GRADE_COLORS[g];
                  const dim = count === 0 ? 0.35 : 1;
                  return (
                    <Tooltip key={g} title={`Grade ${g} · ${count} categor${count === 1 ? 'y' : 'ies'}`}>
                      <Chip
                        label={`${g} · ${count}`}
                        size="small"
                        sx={{
                          opacity: dim,
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          bgcolor: c.bg,
                          color: c.fg,
                          border: `1px solid ${c.main}`
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>

            <Box sx={{ mt: 'auto' }}>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => goToTab('report')}
                sx={{ color: overallColour.fg, textTransform: 'none', fontWeight: 600, px: 0.5 }}
              >
                View full Report
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ============ SHA RISK CATEGORY CARDS ============ */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <MonitorHeartIcon sx={{ color: DARK_GREEN, fontSize: 18 }} />
          <Typography
            variant="overline"
            sx={{ color: DARK_GREEN, fontWeight: 700, letterSpacing: '0.08em' }}
          >
            Risk categories
          </Typography>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Snapshot of the {RISK_CATEGORIES.length} SHA 2.0 assessment areas
          </Typography>
        </Stack>

        <Grid container spacing={1.5}>
          {RISK_CATEGORIES.map((cat) => {
            const c = GRADE_COLORS[cat.grade] || GRADE_COLORS.C;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={cat.key}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    borderLeft: `4px solid ${c.main}`,
                    transition: 'background-color 0.15s, border-color 0.15s',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: c.bg }
                  }}
                  onClick={() => goToTab('report')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goToTab('report');
                    }
                  }}
                >
                  <GradeBadge grade={cat.grade} size="sm" />
                  <Box flex={1} minWidth={0}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                      sx={{ lineHeight: 1.25, mb: 0.5 }}
                    >
                      {cat.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cat.findings?.total ?? 0} finding
                      {(cat.findings?.total ?? 0) === 1 ? '' : 's'}
                      {cat.findings?.criticalHigh
                        ? ` · ${cat.findings.criticalHigh} Critical/High`
                        : ''}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default SHAOverview;
